import type { SdkConfig } from '../core/types';
import type { Api } from '../core/api';
import { redactUrl } from '../core/redact';

const DOWNLOAD_EXTENSIONS = new Set([
	'pdf', 'zip', 'rar', '7z', 'tar', 'gz',
	'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt',
	'csv', 'tsv', 'txt',
	'mp3', 'mp4', 'mov', 'avi', 'wmv',
	'dmg', 'exe', 'apk', 'pkg', 'iso',
]);

function getExtension(href: string): string | null {
	const path = href.split('?')[0].split('#')[0];
	const m = path.match(/\.([a-z0-9]{1,5})$/i);
	return m ? m[1].toLowerCase() : null;
}

/** Filters out text/checkbox/radio inputs that fire click on focus — they
 *  aren't semantically "buttons" and emitting button_click for them is noise. */
function isClickableInput(tag: string, type: string): boolean {
	if (tag === 'BUTTON') return true;
	return type === 'button' || type === 'image' || type === 'reset';
}

function isOutbound(href: string, linkTarget: string | null): boolean {
	try {
		const u = new URL(href, location.href);
		if (u.hostname && u.hostname !== location.hostname) return true;
		if (linkTarget === '_blank') return true;
		return false;
	} catch {
		return false;
	}
}

/** A class token starting with `emblue-`. Token-based, so `not-emblue-thing`
 *  does not count — the edge worker applies the identical rule to
 *  `element_class` when enforcing the `basic` level, and the two must agree. */
const EMBLUE_CLASS = /(^|\s)emblue-/;

function hasEmblueClass(el: Element): boolean {
	const className = (el as HTMLElement).className;
	return typeof className === 'string' && EMBLUE_CLASS.test(className);
}

/** Explicitly marked up for emBlue, by class convention or data attribute. This
 *  is what `clickScope: 'tagged'` binds to. */
function isEmblueTagged(el: Element): boolean {
	return hasEmblueClass(el) || el.hasAttribute('data-emblue-track') || el.hasAttribute('data-emblue-tag');
}

function extractProps(el: Element, e: MouseEvent): Record<string, unknown> {
	const anchor = el as HTMLAnchorElement;
	const href = anchor.href || '';
	let domain: string | undefined;
	let path: string | undefined;
	try {
		if (href) {
			const u = new URL(href, location.href);
			domain = u.hostname;
			path = u.pathname;
		}
	} catch {
		/* swallow */
	}
	const text = ((el as HTMLElement).innerText || el.textContent || '').trim().slice(0, 100);
	// Elements tagged only via data-emblue-* leave no trace in element_class, so
	// flag them explicitly — the worker's `basic` filter needs to tell them apart
	// from untagged elements and can only see what we put in the payload.
	return {
		element_id: el.id || undefined,
		element_class: typeof (el as any).className === 'string' ? (el as any).className : undefined,
		element_emblue_tagged: isEmblueTagged(el) ? true : undefined,
		element_tag: el.tagName.toLowerCase(),
		element_text: text || undefined,
		href: href ? redactUrl(href) : undefined,
		link_domain: domain,
		link_path: path,
		modifier_keys: { ctrl: !!e.ctrlKey, shift: !!e.shiftKey, alt: !!e.altKey, meta: !!e.metaKey },
	};
}

export function installClicks(config: SdkConfig, emblue: Api): void {
	if (!config.autoTrack.clicks) return;
	if (typeof document === 'undefined') return;

	// `all` lets any <a>/<button>/<input> reach the handler; `selective` narrows
	// to links and submit buttons; `tagged` binds only what the site marked up.
	// `[class*="emblue-"]` is a cheap CSS pre-filter — the token test in
	// `hasEmblueClass` is what actually decides, so `not-emblue-x` is excluded.
	const scope = config.autoTrack.clickScope;
	const selector =
		scope === 'tagged'
			? '[data-emblue-track], [data-emblue-tag], [class*="emblue-"]'
			: scope === 'all'
				? '[data-emblue-track], [data-emblue-tag], a, button, input'
				: '[data-emblue-track], [data-emblue-tag], a, button[type="submit"], input[type="submit"]';

	document.addEventListener(
		'click',
		(e: MouseEvent) => {
			const target = e.target as Element | null;
			if (!target || target.nodeType !== 1) return;

			const el = target.closest?.(selector) as Element | null;
			if (!el) return;

			// The `tagged` selector uses a substring match on class for speed;
			// confirm the real token rule before emitting so a class like
			// `not-emblue-thing` doesn't sneak past at the basic level.
			if (scope === 'tagged' && !isEmblueTagged(el)) return;

			const tagAttr = el.getAttribute('data-emblue-tag');
			if (tagAttr) {
				emblue.tag(tagAttr);
			}

			const customName = el.getAttribute('data-emblue-track');
			if (customName) {
				emblue.track(customName, extractProps(el, e));
				return;
			}

			const tag = el.tagName;
			if (tag === 'A' && (el as HTMLAnchorElement).href) {
				const a = el as HTMLAnchorElement;
				const props = extractProps(el, e);
				const ext = getExtension(a.href);
				if (config.autoTrack.downloads && ext && DOWNLOAD_EXTENSIONS.has(ext)) {
					emblue.track('file_download', { ...props, file_extension: ext });
					return;
				}
				if (config.autoTrack.outbound && isOutbound(a.href, a.target || null)) {
					emblue.track('outbound_link', props);
					return;
				}
				// `!== 'selective'`, NOT `=== 'all'`: at the basic level a tagged
				// <a> reaches here with outbound/downloads both off, and gating on
				// 'all' would emit nothing at all for it — the tier would collect
				// zero clicks. There is a regression test for this.
				if (scope !== 'selective') {
					emblue.track('link_click', props);
				}
			} else if (tag === 'BUTTON' || tag === 'INPUT') {
				// Read the HTML attribute, not the IDL property: <button> with no
				// `type` reflects as "submit" via JS but is a regular button to us.
				const typeAttr = (el.getAttribute('type') || '').toLowerCase();
				if (typeAttr === 'submit') {
					emblue.track('form_submit_button', extractProps(el, e));
				} else if (scope !== 'selective' && isClickableInput(tag, typeAttr)) {
					emblue.track('button_click', extractProps(el, e));
				}
			}
		},
		true,
	);
}
